import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "../config";
import { UPDATE_CART_LINE, ADD_TO_CART_LINES } from "../graphqlQueries";
import { ICartItemHeadless, TCartInfo, TSelectedVariantAndDate, TSelectedVariants, TTrackTypes } from "../types";
import { query } from "../lib/shopify";
import { getAdminIdFromStoreFrontId } from "../helpers/shopify";
import { Dispatch } from "react";
import { sortBySku } from "../helpers/utils";

interface CartUpdateResponse {
  cart: any;
  error?: string;
}

// test deploy

interface AddToCartResult {
  success: boolean;
  duplicates?: TSelectedVariants[];
  error?: string;
}

export const updateCartQuantity = async (
  id: string,
  newQuantity: number,
  productRenderedLocation: string,
  headlessCartInfo?: { id: string },
  trackType?: TTrackTypes | null,
  selectedVariants?: TSelectedVariantAndDate | null,
  dispatch?: Dispatch<any> | null
): Promise<CartUpdateResponse> => {
  if (productRenderedLocation === "external") {
    return updateHeadlessCart(id, newQuantity, headlessCartInfo?.id);
  } else {
    if (trackType && selectedVariants && dispatch) {
      return updateShopifyCart(id, newQuantity, trackType, selectedVariants, dispatch);
    } else {
      return updateShopifyCart(id, newQuantity);
    }
  }
};

const updateHeadlessCart = async (lineId: string, quantity: number, cartId?: string): Promise<CartUpdateResponse> => {
  if (!cartId) {
    throw new Error("Cart ID is required for headless cart update");
  }

  const headers = {
    "Content-Type": "application/json",
    "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
  };

  const requestOptions = {
    method: "POST",
    headers,
    body: JSON.stringify({
      query: UPDATE_CART_LINE,
      variables: {
        cartId,
        lineId,
        quantity,
      },
    }),
  };

  try {
    const response = await fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, requestOptions);
    const responseData = await response.json();

    if (response.status === 200) {
      const isCartUpdated = responseData?.data?.cartLinesUpdate;
      if (isCartUpdated) {
        return {
          cart: {
            items: isCartUpdated?.cart?.lines?.edges?.map((edge: { node: any }) => edge.node),
            totalAmount: isCartUpdated?.cart?.cost?.totalAmount?.amount,
          },
        };
      }
      throw new Error("Cart data is missing in the GraphQL response");
    }
    throw new Error(`GraphQL error: ${JSON.stringify(responseData?.errors)}`);
  } catch (error) {
    console.error("GraphQL request failed:", error);
    throw error;
  }
};

const updateShopifyCart = async (
  id: string,
  quantity: number,
  trackType: TTrackTypes | null = null,
  selectedVariants: TSelectedVariantAndDate | null = null,
  dispatch: Dispatch<any> | null = null
): Promise<CartUpdateResponse> => {
  try {
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        updates: {
          [id]: quantity,
        },
      }),
    });

    if (!response.ok) {
      throw new Error("Cart update failed: " + response.statusText);
    }

    const cart = await response.json();

    const { items } = cart;

    if (trackType && selectedVariants && dispatch) {
      if (trackType === "MAIN_TRACK") {
        if (items && items.length > 0) {
          const cartVariantIds = items.map((item: any) => item.id);
          const remainingCartItems = selectedVariants.selectedVariants.filter((item: any) => cartVariantIds.includes(getAdminIdFromStoreFrontId(item.variantsSelectedObject.id)));

          const addSelectedVariants = {
            ...selectedVariants,
            selectedSessionTime: selectedVariants.selectedSessionTime,
            selectedDate: selectedVariants.selectedDate,
            selectedVariants: sortBySku([...remainingCartItems], "MAIN_TRACK"),
          };

          dispatch({
            type: "SET_DATE_AND_VARIANT",
            payload: {
              variantsAndDate: addSelectedVariants,
            },
          });
        } else {
          const addSelectedVariants = {
            ...selectedVariants,
            selectedSessionTime: selectedVariants.selectedSessionTime,
            selectedDate: selectedVariants.selectedDate,
            selectedVariants: [],
          };

          dispatch({
            type: "SET_DATE_AND_VARIANT",
            payload: {
              variantsAndDate: addSelectedVariants,
            },
          });
        }
      } else if (trackType === "MINI_TRACK") {
        if (items && items.length > 0) {
          const cartVariantIds = items.map((item: any) => item.id);
          const remainingCartItems = selectedVariants.selectedVariants.filter((item: any) => cartVariantIds.includes(getAdminIdFromStoreFrontId(item.variantsSelectedObject.id)));

          const addSelectedVariants = {
            ...selectedVariants,
            selectedSessionTime: selectedVariants.selectedSessionTime,
            selectedDate: selectedVariants.selectedDate,
            selectedVariants: sortBySku([...remainingCartItems], "MINI_TRACK"),
          };
          dispatch({
            type: "SET_DATE_AND_VARIANT_JUNIOR_TRACK",
            payload: {
              variantsAndDate: addSelectedVariants,
            },
          });
        } else {
          const addSelectedVariants = {
            ...selectedVariants,
            selectedSessionTime: selectedVariants.selectedSessionTime,
            selectedDate: selectedVariants.selectedDate,
            selectedVariants: [],
          };
          dispatch({
            type: "SET_DATE_AND_VARIANT_MINI_TRACK",
            payload: {
              variantsAndDate: addSelectedVariants,
            },
          });
        }
      } else if (trackType === "JUNIOR_TRACK") {
        if (items && items.length > 0) {
          const cartVariantIds = items.map((item: any) => item.id);
          const remainingCartItems = selectedVariants.selectedVariants.filter((item: any) => cartVariantIds.includes(getAdminIdFromStoreFrontId(item.variantsSelectedObject.id)));

          const addSelectedVariants = {
            ...selectedVariants,
            selectedSessionTime: selectedVariants.selectedSessionTime,
            selectedDate: selectedVariants.selectedDate,
            selectedVariants: sortBySku([...remainingCartItems], "JUNIOR_TRACK"),
          };
          dispatch({
            type: "SET_DATE_AND_VARIANT_MINI_TRACK",
            payload: {
              variantsAndDate: addSelectedVariants,
            },
          });
        } else {
          const addSelectedVariants = {
            ...selectedVariants,
            selectedSessionTime: selectedVariants.selectedSessionTime,
            selectedDate: selectedVariants.selectedDate,
            selectedVariants: [],
          };
          dispatch({
            type: "SET_DATE_AND_VARIANT_JUNIOR_TRACK",
            payload: {
              variantsAndDate: addSelectedVariants,
            },
          });
        }
      } else if (trackType === "VIRTUAL_REALITY") {
        if (items && items.length > 0) {
          const cartVariantIds = items.map((item: any) => item.id);
          const remainingCartItems = selectedVariants.selectedVariants.filter((item: any) => cartVariantIds.includes(getAdminIdFromStoreFrontId(item.variantsSelectedObject.id)));

          const addSelectedVariants = {
            ...selectedVariants,
            selectedSessionTime: selectedVariants.selectedSessionTime,
            selectedDate: selectedVariants.selectedDate,
            selectedVariants: sortBySku([...remainingCartItems], "VIRTUAL_REALITY"),
          };

          dispatch({
            type: "SET_DATE_AND_VARIANT_VIRTUAL_REALITY",
            payload: {
              variantsAndDate: addSelectedVariants,
            },
          });
        } else {
          dispatch({
            type: "SET_DATE_AND_VARIANT_VIRTUAL_REALITY",
            payload: {
              variantsAndDate: {
                ...selectedVariants,
                selectedSessionTime: selectedVariants.selectedSessionTime,
                selectedDate: selectedVariants.selectedDate,
                selectedVariants: [],
              },
            },
          });
        }
      } else if (trackType === "BATTLE_KART_TRACK") {
        if (items && items.length > 0) {
          const cartVariantIds = items.map((item: any) => item.id);
          const remainingCartItems = selectedVariants.selectedVariants.filter((item: any) => cartVariantIds.includes(getAdminIdFromStoreFrontId(item.variantsSelectedObject.id)));

          const addSelectedVariants = {
            ...selectedVariants,
            selectedSessionTime: selectedVariants.selectedSessionTime,
            selectedDate: selectedVariants.selectedDate,
            selectedVariants: sortBySku([...remainingCartItems], "BATTLE_KART_TRACK"),
          };

          dispatch({
            type: "SET_DATE_AND_VARIANT_BATTLE_KART_TRACK",
            payload: {
              variantsAndDate: addSelectedVariants,
            },
          });
        } else {
          const addSelectedVariants = {
            ...selectedVariants,
            selectedSessionTime: selectedVariants.selectedSessionTime,
            selectedDate: selectedVariants.selectedDate,
            selectedVariants: [],
          };
          dispatch({
            type: "SET_DATE_AND_VARIANT_BATTLE_KART_TRACK",
            payload: {
              variantsAndDate: addSelectedVariants,
            },
          });
        }
      }
    }

    if (!cart) {
      throw new Error("Cart update failed: unknown error");
    }

    return { cart };
  } catch (error) {
    console.error("Cart update failed:", error);
    throw error;
  }
};

export const dispatchCartUpdates = (dispatch: any, cartData: any, productRenderedLocation: string) => {
  if (productRenderedLocation === "external") {
    dispatch({
      type: "SET_CARTITEMS_HEADLESS",
      payload: {
        headlessCart: cartData.items as ICartItemHeadless[],
      },
    });

    dispatch({
      type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
      payload: {
        headlessCartTotalAmount: cartData.totalAmount,
      },
    });
  } else {
    if (!cartData.items.length) {
      dispatch({
        type: "SET_IS_CART_EMPTY",
        payload: {
          isCartEmpty: true,
        },
      });
    }

    const cartUpdatedEvent = new CustomEvent("cart:updated", {
      detail: { cart: cartData },
    });
    window.dispatchEvent(cartUpdatedEvent);
  }
};

export const handleAddToCartClick = async (
  selectedVariants: TSelectedVariants[],
  numberOfSeats: number,
  productRenderedLocation: string,
  headlessCart: ICartItemHeadless[],
  headlessCartInfo: TCartInfo,
  dispatch: Dispatch<any>,
  displayMiniCart: boolean = false,
  maxSelectableVariants: number | undefined = undefined
): Promise<AddToCartResult> => {
  try {
    if (!selectedVariants || selectedVariants.length === 0) {
      return {
        success: false,
        error: "Need to select a time",
      };
    }

    if (productRenderedLocation === "external") {
      // Check for duplicates against current cart items
      const cartVariantIds = headlessCart?.map((item) => item.merchandise.id) || [];
      // Find variants that are already in the cart
      const duplicates = selectedVariants.filter((variant) => cartVariantIds.includes(variant.variantsSelectedObject.id));

      if (duplicates.length > 0) {
        return {
          success: false,
          duplicates,
        };
      }

      const lines = {
        items: selectedVariants.map((variant) => ({
          merchandiseId: variant.variantsSelectedObject.id,
          quantity: numberOfSeats,
        })),
      };

      const addToCart = await query(ADD_TO_CART_LINES, {
        cartId: headlessCartInfo.id,
        lines: lines.items,
      });

      const isAddedToCart = addToCart?.data?.cartLinesAdd;

      if (isAddedToCart) {
        const cartItems = isAddedToCart?.cart?.lines?.edges?.map((edge: { node: any }) => edge.node);
        dispatch({
          type: "SET_CARTITEMS_HEADLESS",
          payload: {
            headlessCart: [...cartItems] as ICartItemHeadless[],
          },
        });

        const cartTotalAmount = isAddedToCart?.cart?.cost?.totalAmount?.amount;
        dispatch({
          type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
          payload: {
            headlessCartTotalAmount: cartTotalAmount,
          },
        });
        if (displayMiniCart) {
          dispatch({
            type: "SET_HEADLESS_IS_MINI_CART_SHOWING",
            payload: {
              isHeadlessMiniCartIsShowing: true,
            },
          });
        }
        return { success: true };
      } else {
        console.error("isAddedToCart Cart data is missing in the GraphQL response.", isAddedToCart);
        return {
          success: false,
          error: "Failed to add items to cart",
        };
      }
    } else {
      let formData = {
        items: selectedVariants.map((variant) => ({
          id: getAdminIdFromStoreFrontId(variant.variantsSelectedObject.id),
          quantity: numberOfSeats,
        })),
      };

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      const addedItems = await response.json();

      // Fetch the complete cart data after adding items
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      if (cart && addedItems) {
        miniCartCallback(false);
        const { items_subtotal_price } = cart;

        if (items_subtotal_price) {
          dispatch({
            type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
            payload: {
              headlessCartTotalAmount: items_subtotal_price,
            },
          });
        }

        return { success: true };
      }
      return {
        success: false,
        error: "Failed to add items to cart",
      };
    }
  } catch (error) {
    console.error("Error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An unknown error occurred",
    };
  }
};
